import { SearchItem, PageInfo, AdvancedSearchParams, AdvancedSearchResponse } from "./useAdvancedSearch";
import { AppliedCoupon } from "~/modules/GraphQL/types";
import { postApiAxios, postDataAxios } from "~/helpers";
import api from "~/config/extensionsApi";
import { ref } from "@nuxtjs/composition-api";

/**
 * Composable for handling advanced search functionality
 */
export const useAdvancedSearch = () => {
    const loading = ref(false);
    const error = ref<string | null>(null);
    const searchResults = ref<SearchItem[]>([]);
    const totalCount = ref(0);
    const pageInfo = ref<PageInfo>({
      current_page: 1,
      page_size: 5,
      total_pages: 1
    });
  
    /**
     * Fetch search results from the API
     */
    const fetchSearchResults = async (keyword: string = '', page: number = 1, pageSize: number = 5) => {
      loading.value = true;
      error.value = null;
      
      try {
        const params: AdvancedSearchParams = {
          variables: {
            keyword,
            currentPage: page,
            pageSize
          }
        };

        // Make the API call
        const response = await postApiAxios(api.advancedSearch.get_advanced_search, params);
        
        // Check the response more carefully
        if (response && response.data) {
          // Access the advancedSearch data directly from response.data
          const { advancedSearch } = response.data;
          
          if (advancedSearch && Array.isArray(advancedSearch.items)) {
            // Update state with search results
            searchResults.value = advancedSearch.items;
            totalCount.value = advancedSearch.total_count || 0;
            pageInfo.value = advancedSearch.page_info || {
              current_page: page,
              page_size: pageSize,
              total_pages: 1
            };
          } else {
            throw new Error('Invalid search results structure');
          }
        } else if (response) {
          // If response exists but data is missing, try to access advancedSearch directly
          if (response.advancedSearch && Array.isArray(response.advancedSearch.items)) {
            searchResults.value = response.advancedSearch.items;
            totalCount.value = response.advancedSearch.total_count || 0;
            pageInfo.value = response.advancedSearch.page_info || {
              current_page: page,
              page_size: pageSize,
              total_pages: 1
            };
          } else {
            throw new Error('Invalid response structure from server');
          }
        } else {
          throw new Error('No response received from server');
        }
      } catch (err) {
        error.value = err instanceof Error ? err.message : 'Failed to fetch search results';
        // Reset the results on error
        searchResults.value = [];
        totalCount.value = 0;
      } finally {
        loading.value = false;
      }
    };
  
    return {
      loading,
      error,
      searchResults,
      totalCount,
      pageInfo,
      fetchSearchResults
    };
  };
  
  export default useAdvancedSearch;
